// @ts-check

/** @file Pure first-sync activity card state machine for one board screen. */

/** @import {SyncCoordinatorState} from "velocious/build/src/sync/sync-coordinator-types.js" */

/**
 * A screen-projected coordinator snapshot for the activity card.
 * @typedef {{projectId: string | null, state: SyncCoordinatorState, lastSuccessAt: string | null}} ProjectSyncActivitySnapshot
 */

/**
 * An ordered action applied to the flash-notifications activity handle.
 * @typedef {"show" | "succeed" | "fail" | "done"} ProjectSyncActivityAction
 */

/**
 * Tracks the initial sync card for one screen lifetime.
 * @typedef {{shownProjectId: string | null, resolvedProjectId: string | null, transition: (snapshot: ProjectSyncActivitySnapshot) => ProjectSyncActivityAction[]}} ProjectSyncActivityTracker
 */

/**
 * Creates an isolated tracker with no React or notification dependencies.
 * @returns {ProjectSyncActivityTracker} Fresh tracker state machine.
 */
export function createProjectSyncActivityTracker() {
  /**
   * @type {ProjectSyncActivityTracker}
   */
  const tracker = {
    /** @type {string | null} */
    shownProjectId: null,
    /** @type {string | null} */
    resolvedProjectId: null,

    /**
     * Advances the state machine with one coordinator snapshot.
     * @param {ProjectSyncActivitySnapshot} snapshot Screen project and coordinator state.
     * @returns {ProjectSyncActivityAction[]} Ordered handle actions, possibly empty.
     */
    transition(snapshot) {
      const actions = /** @type {ProjectSyncActivityAction[]} */ ([])
      const {lastSuccessAt, projectId, state} = snapshot

      if (this.shownProjectId != null && this.shownProjectId !== projectId) {
        actions.push("done")
        this.shownProjectId = null
        this.resolvedProjectId = null
      }
      if (projectId == null) return actions
      if (this.shownProjectId === projectId) {
        if (state === "idle" && lastSuccessAt != null) {
          this.shownProjectId = null
          this.resolvedProjectId = projectId
          actions.push("succeed")
        } else if (state === "backoff" || state === "failed") {
          this.shownProjectId = null
          this.resolvedProjectId = projectId
          actions.push("fail")
        } else if (state === "stopped" || state === "offline") {
          this.shownProjectId = null
          this.resolvedProjectId = projectId
          actions.push("done")
        }
        return actions
      }
      if (this.resolvedProjectId === projectId) return actions
      if (state === "pending" || state === "syncing") {
        this.shownProjectId = projectId
        actions.push("show")
      } else if (state === "idle" && lastSuccessAt != null) {
        this.resolvedProjectId = projectId
      } else if (state === "backoff" || state === "failed") {
        this.resolvedProjectId = projectId
      }
      return actions
    }
  }

  return tracker
}
